import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  PackageCheck, ScanLine, ArrowLeft, Printer, Check, X, RefreshCw, Search, Trash2, Plus, Minus, FileText,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtQ = (n) => Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 3 });

const ESTADO_BADGE = {
  pendiente: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  parcial: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  entregado: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelado: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const TXT = {
  venta: {
    titulo: "Entregas pendientes",
    accion: "Entregar",
    confirmar: "Confirmar entrega",
    entregada: "Entregada",
    aEntregar: "A entregar ahora",
    constancia: "CONSTANCIA DE ENTREGA",
    entidad: "Cliente",
    docLabel: "Factura/Boleta",
    firmaA: "Entregado por",
    firmaB: "Recibí conforme (cliente)",
    vacio: "No hay mercadería pendiente de entrega.",
    escanear: "Escaneá el código de barras o escribí el SKU y presioná Enter",
    historial: "Entregas realizadas",
    hist1: "ENT",
  },
  compra: {
    titulo: "Mercaderías por recibir",
    accion: "Recibir",
    confirmar: "Confirmar recepción",
    entregada: "Recibida",
    aEntregar: "A recibir ahora",
    constancia: "NOTA DE RECEPCIÓN",
    entidad: "Proveedor",
    docLabel: "Compra",
    firmaA: "Recibido por",
    firmaB: "Entregado por (proveedor/transportista)",
    vacio: "No hay mercadería pendiente de recibir.",
    escanear: "Escaneá el código de barras o escribí el SKU y presioná Enter",
    historial: "Recepciones realizadas",
    hist1: "REC",
  },
};

function ConfigDefaults({ headers, logoTipo }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState({ entrega_posterior_default_ventas: false, entrega_posterior_default_compras: false });
  const qs = logoTipo ? `?logo_tipo=${logoTipo}` : "";
  useEffect(() => {
    if (!open) return;
    fetch(`${API}/admin/entregas/config${qs}`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setCfg((c) => ({ ...c, ...d })))
      .catch(() => {});
  }, [open]); // eslint-disable-line
  const guardar = async (next) => {
    setCfg(next);
    try {
      const r = await fetch(`${API}/admin/entregas/config${qs}`, { method: "PUT", headers, body: JSON.stringify({ ...next, logo_tipo: logoTipo || undefined }) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); toast.error(e.detail || "No se pudo guardar"); }
      else toast.success("Configuración guardada");
    } catch { toast.error("Error de conexión"); }
  };
  return (
    <div className="border border-white/10 rounded-xl">
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-2 text-xs text-slate-300 hover:text-white font-body">
        {open ? "▾" : "▸"} Configuración de entrega posterior (por empresa)
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2 text-sm text-slate-200 font-body">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!cfg.entrega_posterior_default_ventas}
              onChange={(e) => guardar({ ...cfg, entrega_posterior_default_ventas: e.target.checked })} />
            Marcar "Entrega posterior" por defecto en Facturas/Boletas nuevas
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!cfg.entrega_posterior_default_compras}
              onChange={(e) => guardar({ ...cfg, entrega_posterior_default_compras: e.target.checked })} />
            Marcar "Por recibir" por defecto en Compras nuevas
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * Panel de Entregas (venta) / Recepciones (compra) de mercadería pendiente.
 * Props: tipo ("venta"|"compra"), token, logoTipo, hasPermission, isAdmin, initialOrigenId
 */
export default function EntregasPanel({ tipo = "venta", token, logoTipo, hasPermission, isAdmin, initialOrigenId = null }) {
  const T = TXT[tipo];
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);
  const canCrear = isAdmin || hasPermission("entregas.crear");
  const canAnular = isAdmin || hasPermission("entregas.anular");

  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState("abiertos");
  const [openId, setOpenId] = useState(initialOrigenId);

  const fetchLista = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ tipo, estado });
      if (logoTipo) p.set("logo_tipo", logoTipo);
      if (search.trim()) p.set("search", search.trim());
      const r = await fetch(`${API}/admin/entregas/pendientes?${p}`, { headers });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "No se pudo cargar la lista");
        setLista([]);
      } else setLista(await r.json());
    } catch { toast.error("Error de conexión"); setLista([]); }
    setLoading(false);
  }, [tipo, estado, search, logoTipo, headers]);

  useEffect(() => { if (!openId) fetchLista(); }, [openId, estado, logoTipo, tipo]); // eslint-disable-line
  useEffect(() => { setOpenId(initialOrigenId); }, [initialOrigenId]);

  if (openId) {
    return (
      <EntregaDetalle
        tipo={tipo} T={T} origenId={openId} headers={headers} canCrear={canCrear} canAnular={canAnular}
        onBack={() => { setOpenId(null); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {(isAdmin || hasPermission("entregas.configurar")) && (
        <ConfigDefaults headers={headers} logoTipo={logoTipo} />
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchLista()}
            placeholder={`Buscar por N° o ${T.entidad.toLowerCase()}…`}
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white font-body outline-none focus:border-emerald-500/50"
          />
        </div>
        <select
          value={estado} onChange={(e) => setEstado(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-body"
        >
          <option value="abiertos">Con pendiente</option>
          <option value="pendiente">Sin entregar</option>
          <option value="parcial">Parciales</option>
          <option value="entregado">Completadas</option>
          <option value="todos">Todas</option>
        </select>
        <button onClick={fetchLista} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:text-white text-sm font-body">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      <div className="border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-slate-400 text-xs uppercase font-body">
            <tr>
              <th className="text-left px-4 py-2.5">N°</th>
              <th className="text-left px-4 py-2.5">Fecha</th>
              <th className="text-left px-4 py-2.5">{T.entidad}</th>
              <th className="text-left px-4 py-2.5 w-56">Avance</th>
              <th className="text-right px-4 py-2.5">Pendiente</th>
              <th className="text-left px-4 py-2.5">Estado</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-500 py-8 font-body">{loading ? "Cargando…" : T.vacio}</td></tr>
            )}
            {lista.map((r) => (
              <tr key={r.id} className="border-t border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setOpenId(r.id)}>
                <td className="px-4 py-2.5 font-mono text-slate-200">
                  {r.numero || "—"}
                  {r.sin_factura && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300">Boleta</span>}
                  {r.tiene_borrador && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">Borrador</span>}
                </td>
                <td className="px-4 py-2.5 text-slate-300">{r.fecha}</td>
                <td className="px-4 py-2.5 text-slate-200">{r.entidad_nombre}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, r.avance_pct)}%` }} />
                    </div>
                    <span className="text-xs text-slate-400 w-24 text-right">{fmtQ(r.entregadas)} / {fmtQ(r.total_unidades)}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right text-amber-300 font-semibold">{fmtQ(r.pendientes)}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${ESTADO_BADGE[r.estado_entrega] || ESTADO_BADGE.pendiente}`}>
                    {r.estado_entrega === "pendiente" ? "Pendiente" : r.estado_entrega === "parcial" ? "Parcial" : r.estado_entrega === "entregado" ? (tipo === "venta" ? "Entregado" : "Recibido") : "Cancelado"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button className="text-emerald-300 hover:text-emerald-200 text-xs font-medium">Abrir →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function EntregaDetalle({ tipo, T, origenId, headers, canCrear, canAnular, onBack }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});      // { linea_idx: cantidad }
  const [notas, setNotas] = useState("");
  const [receptor, setReceptor] = useState("");
  const [scan, setScan] = useState("");
  const [flash, setFlash] = useState(null);    // linea_idx recién escaneada
  const [busy, setBusy] = useState(false);
  const [anular, setAnular] = useState(null);  // entrega a anular
  const [motivo, setMotivo] = useState("");
  const scanRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/entregas/origen/${tipo}/${origenId}`, { headers });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "No se pudo cargar el documento");
        onBack();
        return;
      }
      const d = await r.json();
      setData(d);
      const m = {};
      (d.borrador?.items || []).forEach((i) => { m[i.linea_idx] = i.cantidad; });
      setDraft(m);
      setNotas(d.borrador?.notas || "");
      setReceptor(d.borrador?.receptor_nombre || "");
    } catch { toast.error("Error de conexión"); }
  }, [tipo, origenId, headers]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (data && canCrear) scanRef.current?.focus(); }, [data, canCrear]);

  const guardarBorrador = async (nuevo, extra = {}) => {
    const items = Object.entries(nuevo).filter(([, q]) => Number(q) > 0).map(([i, q]) => ({ linea_idx: Number(i), cantidad: Number(q) }));
    try {
      const r = await fetch(`${API}/admin/entregas/borrador/${tipo}/${origenId}`, {
        method: "PUT", headers, body: JSON.stringify({ items, notas: extra.notas ?? notas, receptor_nombre: extra.receptor ?? receptor }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "No se pudo guardar el borrador");
        await cargar();
        return false;
      }
      return true;
    } catch { toast.error("Error de conexión"); return false; }
  };

  const setQty = async (idx, val, max) => {
    let q = Number(val);
    if (!Number.isFinite(q) || q < 0) q = 0;
    if (q > max) { toast.error(`Solo quedan ${fmtQ(max)} pendientes`); q = max; }
    const nuevo = { ...draft, [idx]: q };
    setDraft(nuevo);
    await guardarBorrador(nuevo);
  };

  const onScan = async (e) => {
    e.preventDefault();
    const codigo = scan.trim();
    if (!codigo || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/entregas/borrador/${tipo}/${origenId}/escanear`, {
        method: "POST", headers, body: JSON.stringify({ codigo, cantidad: 1 }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) toast.error(d.detail || "Código no válido");
      else {
        const m = {};
        (d.borrador?.items || []).forEach((i) => { m[i.linea_idx] = i.cantidad; });
        setDraft(m);
        setFlash(d.linea_idx);
        setTimeout(() => setFlash(null), 700);
      }
    } catch { toast.error("Error de conexión"); }
    setScan("");
    setBusy(false);
    scanRef.current?.focus();
  };

  const llenarTodo = async () => {
    const m = {};
    (data?.lineas || []).forEach((l) => { if (l.pendiente > 0) m[l.linea_idx] = l.pendiente; });
    setDraft(m);
    await guardarBorrador(m);
  };

  const descartar = async () => {
    setDraft({});
    await fetch(`${API}/admin/entregas/borrador/${tipo}/${origenId}`, { method: "DELETE", headers });
    toast.success("Borrador descartado");
    cargar();
  };

  const totalDraft = Object.values(draft).reduce((a, b) => a + Number(b || 0), 0);

  const confirmar = async () => {
    if (totalDraft <= 0) { toast.error("Cargá al menos una unidad"); return; }
    if (!window.confirm(`${T.confirmar}: ${fmtQ(totalDraft)} unidades. ¿Confirmás? El stock se mueve recién ahora.`)) return;
    setBusy(true);
    const ok = await guardarBorrador(draft, { notas, receptor });
    if (!ok) { setBusy(false); return; }
    try {
      const r = await fetch(`${API}/admin/entregas/borrador/${tipo}/${origenId}/confirmar`, { method: "POST", headers });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) toast.error(d.detail || "No se pudo confirmar");
      else {
        toast.success(`${d.entrega?.numero} confirmada`);
        await cargar();
        imprimir(d.entrega?.id);
      }
    } catch { toast.error("Error de conexión"); }
    setBusy(false);
  };

  const imprimir = async (entregaId) => {
    if (!entregaId) return;
    try {
      const r = await fetch(`${API}/admin/entregas/${entregaId}`, { headers });
      if (!r.ok) { toast.error("No se pudo cargar la constancia"); return; }
      const e = await r.json();
      const emp = e.empresa || {};
      const empNombre = emp.nombre_fantasia || emp.razon_social || emp.nombre || "";
      const filas = (e.items || []).map((i, n) => `
        <tr><td>${n + 1}</td><td>${(i.sku || "")}</td><td>${(i.descripcion || "")}${i.variante_texto ? ` — ${i.variante_texto}` : ""}</td><td style="text-align:right">${fmtQ(i.cantidad)}</td></tr>`).join("");
      const total = (e.items || []).reduce((a, i) => a + Number(i.cantidad || 0), 0);
      const w = window.open("", "_blank", "width=800,height=900");
      if (!w) { toast.error("Habilitá las ventanas emergentes para imprimir"); return; }
      w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${e.numero}</title>
        <style>body{font-family:Arial,sans-serif;font-size:12px;padding:24px;color:#111}
        h1{font-size:18px;margin:0}h2{font-size:14px;margin:2px 0 12px;font-weight:normal}
        table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #444;padding:5px 7px}
        th{background:#eee;text-align:left}.meta td{border:none;padding:2px 4px}
        .firmas{display:flex;gap:40px;margin-top:70px}.firmas div{flex:1;border-top:1px solid #000;text-align:center;padding-top:4px}
        .nota{margin-top:10px;font-size:11px;color:#444}</style></head><body>
        <h1>${empNombre}</h1><h2>${emp.ruc ? "RUC: " + emp.ruc : ""} ${emp.direccion || ""}</h2>
        <h1>${T.constancia} N° ${e.numero}</h1>
        <table class="meta"><tr><td><b>Fecha:</b> ${e.fecha || ""}</td><td><b>${T.docLabel}:</b> ${e.origen_numero || ""}</td></tr>
        <tr><td colspan="2"><b>${T.entidad}:</b> ${e.entidad_nombre || ""}</td></tr>
        ${e.receptor_nombre ? `<tr><td colspan="2"><b>Recibe:</b> ${e.receptor_nombre}</td></tr>` : ""}</table>
        <table><thead><tr><th>#</th><th>SKU</th><th>Descripción</th><th style="text-align:right">Cantidad</th></tr></thead><tbody>${filas}
        <tr><td colspan="3" style="text-align:right"><b>Total unidades</b></td><td style="text-align:right"><b>${fmtQ(total)}</b></td></tr></tbody></table>
        ${e.notas ? `<p class="nota"><b>Observaciones:</b> ${e.notas}</p>` : ""}
        <p class="nota">Documento interno de control de mercadería. No es un comprobante fiscal.</p>
        <div class="firmas"><div>${T.firmaA}<br/>${e.confirmada_por || ""}</div><div>${T.firmaB}</div></div>
        <script>window.onload=function(){window.print();}</script></body></html>`);
      w.document.close();
    } catch { toast.error("Error al imprimir"); }
  };

  const anularEntrega = async () => {
    if (!motivo.trim()) { toast.error("Ingresá el motivo"); return; }
    try {
      const r = await fetch(`${API}/admin/entregas/${anular.id}/anular`, { method: "POST", headers, body: JSON.stringify({ motivo }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) toast.error(d.detail || "No se pudo anular");
      else { toast.success(`${anular.numero} anulada: vuelve a pendiente`); setAnular(null); setMotivo(""); cargar(); }
    } catch { toast.error("Error de conexión"); }
  };

  if (!data) return <div className="text-slate-400 text-sm py-10 text-center font-body">Cargando…</div>;
  const o = data.origen;
  const completo = o.pendientes <= 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="flex items-center gap-1 text-slate-300 hover:text-white text-sm font-body">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex-1" />
        <span className={`text-xs px-2 py-0.5 rounded-full border ${ESTADO_BADGE[o.estado_entrega] || ESTADO_BADGE.pendiente}`}>
          {o.estado_entrega === "entregado" ? (tipo === "venta" ? "Entregado" : "Recibido") : o.estado_entrega === "parcial" ? "Parcial" : "Pendiente"}
        </span>
      </div>

      {/* Cabecera estilo remisión */}
      <div className="border border-white/10 rounded-xl bg-white/5 p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-body">
        <div><div className="text-xs text-slate-500 uppercase">{T.docLabel}</div><div className="text-white font-mono">{o.numero || "—"}{o.sin_factura ? " (boleta)" : ""}</div></div>
        <div><div className="text-xs text-slate-500 uppercase">Fecha</div><div className="text-white">{o.fecha}</div></div>
        <div className="col-span-2"><div className="text-xs text-slate-500 uppercase">{T.entidad}</div><div className="text-white">{o.entidad_nombre}</div></div>
        <div className="col-span-2 md:col-span-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, o.avance_pct)}%` }} /></div>
            <span className="text-xs text-slate-300">{fmtQ(o.entregadas)} de {fmtQ(o.total_unidades)} · pendiente {fmtQ(o.pendientes)}</span>
          </div>
        </div>
      </div>

      {/* Escáner */}
      {canCrear && !completo && (
        <form onSubmit={onScan} className="flex items-center gap-2">
          <div className="relative flex-1">
            <ScanLine className="w-5 h-5 text-emerald-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={scanRef} value={scan} onChange={(e) => setScan(e.target.value)} autoComplete="off"
              placeholder={T.escanear}
              className="w-full bg-emerald-500/5 border border-emerald-500/30 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white font-mono outline-none focus:border-emerald-400"
            />
          </div>
          <button type="button" onClick={llenarTodo} className="px-3 py-2.5 rounded-lg border border-white/10 text-slate-200 hover:text-white text-sm font-body whitespace-nowrap">
            {T.accion} todo lo pendiente
          </button>
        </form>
      )}

      {/* Líneas */}
      <div className="border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-slate-400 text-xs uppercase font-body">
            <tr>
              <th className="text-left px-4 py-2.5">Producto</th>
              <th className="text-right px-3 py-2.5">{tipo === "venta" ? "Facturada" : "Comprada"}</th>
              <th className="text-right px-3 py-2.5">{T.entregada}</th>
              <th className="text-center px-3 py-2.5 w-44">{T.aEntregar}</th>
              <th className="text-right px-3 py-2.5">Queda</th>
            </tr>
          </thead>
          <tbody>
            {data.lineas.map((l) => {
              const q = Number(draft[l.linea_idx] || 0);
              const queda = Math.max(0, l.pendiente - q);
              const verde = l.pendiente <= 0 || (q > 0 && queda <= 0);
              return (
                <tr key={l.linea_idx} className={`border-t border-white/5 transition-colors ${flash === l.linea_idx ? "bg-emerald-400/25" : verde ? "bg-emerald-500/10" : ""}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-white font-body">{l.descripcion}{l.variante_texto ? <span className="text-slate-400"> — {l.variante_texto}</span> : null}</div>
                    <div className="text-xs text-slate-500 font-mono">{l.sku}{l.codigo_barras ? ` · ${l.codigo_barras}` : ""}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-200">{fmtQ(l.cantidad)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-200">
                    {fmtQ(l.entregada)}
                    {l.devuelta > 0 && <div className="text-[10px] text-rose-300">devuelta {fmtQ(l.devuelta)}</div>}
                    {l.cancelada > 0 && <div className="text-[10px] text-slate-400">cancelada {fmtQ(l.cancelada)}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    {l.pendiente > 0 && canCrear ? (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => setQty(l.linea_idx, q - 1, l.pendiente)} className="p-1 rounded bg-white/10 hover:bg-white/20 text-white"><Minus className="w-3.5 h-3.5" /></button>
                        <input
                          type="number" min={0} max={l.pendiente} step="any" value={q || ""}
                          onChange={(e) => setDraft({ ...draft, [l.linea_idx]: e.target.value })}
                          onBlur={(e) => setQty(l.linea_idx, e.target.value, l.pendiente)}
                          className="w-16 text-center bg-white/5 border border-white/10 rounded px-1 py-1 text-white text-sm outline-none focus:border-emerald-500/50"
                        />
                        <button onClick={() => setQty(l.linea_idx, q + 1, l.pendiente)} className="p-1 rounded bg-white/10 hover:bg-white/20 text-white"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : <div className="text-center text-slate-500">—</div>}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {verde ? <span className="inline-flex items-center gap-1 text-emerald-300 font-semibold"><Check className="w-4 h-4" />{l.pendiente <= 0 ? "Completo" : "0"}</span>
                      : <span className="text-amber-300 font-semibold">{fmtQ(queda)}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {canCrear && !completo && (
        <div className="grid md:grid-cols-2 gap-3">
          <input
            value={receptor} onChange={(e) => setReceptor(e.target.value)} onBlur={() => guardarBorrador(draft, { receptor })}
            placeholder={tipo === "venta" ? "Quién retira (opcional)" : "Quién entrega / transportista (opcional)"}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-body outline-none focus:border-emerald-500/50"
          />
          <input
            value={notas} onChange={(e) => setNotas(e.target.value)} onBlur={() => guardarBorrador(draft, { notas })}
            placeholder="Observaciones (opcional)"
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-body outline-none focus:border-emerald-500/50"
          />
        </div>
      )}

      {canCrear && !completo && (
        <div className="flex items-center gap-3 justify-end">
          <span className="text-sm text-slate-300 font-body">Total cargado: <b className="text-white">{fmtQ(totalDraft)}</b></span>
          {data.borrador && (
            <button onClick={descartar} className="flex items-center gap-1 px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:text-rose-300 text-sm font-body">
              <Trash2 className="w-4 h-4" /> Descartar borrador
            </button>
          )}
          <button
            onClick={confirmar} disabled={busy || totalDraft <= 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-semibold font-body"
          >
            <PackageCheck className="w-4 h-4" /> {T.confirmar}
          </button>
        </div>
      )}

      {/* Historial */}
      <div>
        <h3 className="text-sm font-semibold text-slate-200 font-body mb-2">{T.historial}</h3>
        <div className="border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {(data.historial || []).length === 0 && (
                <tr><td className="text-center text-slate-500 py-5 font-body">Todavía no hay movimientos confirmados.</td></tr>
              )}
              {(data.historial || []).map((h) => (
                <tr key={h.id} className="border-t border-white/5 first:border-t-0">
                  <td className={`px-4 py-2.5 font-mono ${h.estado === "anulada" ? "text-slate-500 line-through" : "text-slate-200"}`}>{h.numero}</td>
                  <td className="px-3 py-2.5 text-slate-300">{h.fecha}</td>
                  <td className="px-3 py-2.5 text-slate-400">{(h.items || []).reduce((a, i) => a + Number(i.cantidad || 0), 0)} un. · {h.confirmada_por}</td>
                  <td className="px-3 py-2.5 text-xs">
                    {h.estado === "anulada" ? <span className="text-rose-300">Anulada: {h.motivo_anulacion}</span> : <span className="text-emerald-300">Confirmada</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => imprimir(h.id)} className="inline-flex items-center gap-1 text-slate-300 hover:text-white text-xs mr-3"><Printer className="w-3.5 h-3.5" /> Imprimir</button>
                    {canAnular && h.estado === "confirmada" && (
                      <button onClick={() => { setAnular(h); setMotivo(""); }} className="inline-flex items-center gap-1 text-rose-300 hover:text-rose-200 text-xs"><X className="w-3.5 h-3.5" /> Anular</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {anular && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-white/10 rounded-xl p-5 w-full max-w-md space-y-3">
            <h4 className="text-white font-semibold font-body flex items-center gap-2"><FileText className="w-4 h-4" /> Anular {anular.numero}</h4>
            <p className="text-xs text-slate-400 font-body">
              {tipo === "venta" ? "La mercadería vuelve a quedar pendiente de entrega (el stock se devuelve)." : "Se descuenta el stock recibido y la compra vuelve a quedar pendiente."}
            </p>
            <textarea
              value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Motivo (obligatorio)"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-body outline-none"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setAnular(null)} className="px-3 py-2 rounded-lg border border-white/10 text-slate-300 text-sm">Cancelar</button>
              <button onClick={anularEntrega} className="px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold">Anular</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
